(() => {
  const tag = 'ggx-nerdrotic-neon-sign';
  if (!window.customElements || customElements.get(tag)) return;
  const signs = new Set();
  let boundaryTimer = null;
  let globalAbort = null;

  function clearBoundary() {
    if (boundaryTimer !== null) clearTimeout(boundaryTimer);
    boundaryTimer = null;
  }

  function refreshAll() {
    clearBoundary();
    const now = Date.now();
    const core = window.GGXLivingWorldsCore;
    let state = 'unknown';
    try {
      const next = core?.stateAt(now);
      if (next === 'day' || next === 'night') state = next;
    } catch { }
    for (const sign of signs) {
      sign.dataset.neonState = state;
      sign.align();
    }
    if (!signs.size || document.hidden) return;
    try {
      const delay = core?.nextBoundary(now) - now;
      if (Number.isFinite(delay) && delay > 0) boundaryTimer = setTimeout(refreshAll, Math.min(2147483647, delay + 20));
    } catch { }
  }

  function subscribe(sign) {
    if (!signs.size) {
      globalAbort = new AbortController();
      const options = { signal: globalAbort.signal };
      document.addEventListener('visibilitychange', refreshAll, options);
      document.addEventListener('shopify:section:load', refreshAll, options);
      document.addEventListener('shopify:section:unload', refreshAll, options);
      window.addEventListener('pageshow', refreshAll, options);
      window.addEventListener('focus', refreshAll, options);
      window.addEventListener('pagehide', clearBoundary, options);
    }
    signs.add(sign);
    refreshAll();
  }

  function unsubscribe(sign) {
    signs.delete(sign);
    if (signs.size) return;
    clearBoundary();
    globalAbort?.abort();
    globalAbort = null;
  }

  class NerdroticNeonSign extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.world = this.dataset.signPlacement === 'home' ? null : this.closest('ggx-living-world');
      if (this.world) {
        this.viewport = window.matchMedia?.('(max-width: 749px)');
        this.viewport?.addEventListener('change', () => this.align(), options);
        this.world.addEventListener('load', () => this.align(), { ...options, capture: true });
        window.addEventListener('resize', () => this.align(), options);
        if (typeof ResizeObserver === 'function') {
          this.resizeObserver = new ResizeObserver(() => this.align());
          this.resizeObserver.observe(this.world);
          const frame = this.world.querySelector('[data-living-frame]');
          if (frame) this.resizeObserver.observe(frame);
        }
        if (typeof MutationObserver === 'function') {
          this.sceneObserver = new MutationObserver(() => this.align());
          this.sceneObserver.observe(this.world, { attributes: true, attributeFilter: ['data-mobile', 'data-state', 'data-fallback'] });
          const frame = this.world.querySelector('[data-living-frame]');
          if (frame) this.sceneObserver.observe(frame, { childList: true });
        }
      }
      subscribe(this);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      this.sceneObserver?.disconnect();
      this.resizeObserver = null;
      this.sceneObserver = null;
      this.world = null;
      unsubscribe(this);
    }

    align() {
      const core = window.GGXLivingWorldsCore;
      const world = this.world;
      if (!world || !core?.fitPlane || !this.isConnected) return;
      const image = world.querySelector('[data-living-frame] img');
      if (!image) return;
      const portrait = Boolean(this.viewport?.matches && world.dataset.mobile === 'true');
      const source = portrait ? { width: 1024, height: 1536 } : { width: 1672, height: 941 };
      const mount = portrait ? { x: 284, y: 121, width: 500, height: 119 } : { x: 620, y: 51, width: 468, height: 93 };
      const style = getComputedStyle(image);
      const position = style.objectPosition.split(' ');
      const fraction = (value, fallback) => value?.endsWith('%') ? parseFloat(value) / 100 : fallback;
      const imageRect = image.getBoundingClientRect();
      const worldRect = world.getBoundingClientRect();
      const plane = core.fitPlane({ width: imageRect.width, height: imageRect.height, sourceWidth: source.width, sourceHeight: source.height, fit: style.objectFit === 'contain' ? 'contain' : 'cover', positionX: fraction(position[0], .5), positionY: fraction(position[1], .5) });
      if (!plane) return;
      Object.assign(this.style, {
        left: `${imageRect.left - worldRect.left + plane.left + mount.x * plane.width / source.width}px`,
        top: `${imageRect.top - worldRect.top + plane.top + mount.y * plane.height / source.height}px`,
        width: `${mount.width * plane.width / source.width}px`,
        height: `${mount.height * plane.height / source.height}px`
      });
    }
  }

  customElements.define(tag, NerdroticNeonSign);
})();
